import { CheckCircleOutlined, Circle, Close } from '@mui/icons-material'
import { Grid, Typography, useTheme } from '@mui/material'
import { FormikErrors, FormikTouched } from 'formik'
import * as Yup from 'yup'

import usePasswordStyles from './password-validation.styles'

const MIN_LENGTH_ERROR = 'must be at least 8 characters'
const AT_LEAST_ONE_NUMBER_ERROR = 'must contain at least one number'
const TWO_CONSECUTIVE_CHARS_ERROR = 'must not contain more than two consecutive identical characters'

interface ChangePasswordFormValues {
  currentPassword: string
  newPassword: string
}
interface ResetPasswordFormValues {
  newPassword: string
}
type FormValues = ChangePasswordFormValues | ResetPasswordFormValues

export const validate = async (values: FormValues) => {
  const newPasswordValidation = Yup.string()
    .min(8, MIN_LENGTH_ERROR)
    .matches(/[0-9]/, AT_LEAST_ONE_NUMBER_ERROR)
    .test(TWO_CONSECUTIVE_CHARS_ERROR, TWO_CONSECUTIVE_CHARS_ERROR, (value) => !/(.)\1\1/.test(String(value)))
  const schema = Yup.object(
    (values as ChangePasswordFormValues).currentPassword
      ? {
          currentPassword: Yup.string().required(),
          newPassword: newPasswordValidation,
        }
      : {
          newPassword: newPasswordValidation,
        }
  )

  try {
    await schema.validate(values, { abortEarly: false })
  } catch (e) {
    if (e instanceof Yup.ValidationError) {
      return { newPassword: (e as Yup.ValidationError).errors.join(',') }
    }
  }

  // no errors
  return {}
}

export const isPasswordCriteriaMet = (formikErrors: FormikErrors<FormValues>, touched: boolean | undefined, errorMessage: string): boolean => {
  let result = false
  if (touched && (Object.keys(formikErrors).length === 0 || !formikErrors.newPassword?.includes(errorMessage))) {
    result = true
  }

  return result
}

export const usePasswordValidation = (errors: FormikErrors<FormValues>, touched: FormikTouched<FormValues>) => {
  const passwordClasses = usePasswordStyles
  const theme = useTheme()

  const getIcon = (formikErrors: FormikErrors<FormValues>, errorMessage: string) => {
    let result = (
      <div style={passwordClasses.passwordCriteriaBulletPoint}>
        <Circle sx={passwordClasses.passwordCriteriaBulletPointIcon} />
      </div>
    )

    if (touched && formikErrors.newPassword?.includes(errorMessage)) {
      result = <Close color="error" fontSize="small" sx={passwordClasses.passwordCriteriaIcon} aria-label="Password criteria failure icon" />
    }
    if (isPasswordCriteriaMet(formikErrors, touched.newPassword, errorMessage)) {
      result = <CheckCircleOutlined color="success" fontSize="small" sx={passwordClasses.passwordCriteriaIcon} aria-label="Password criteria success icon" />
    }

    return result
  }

  return (
    <>
      <Grid size={12}>
        <Typography variant="subtitle2">Password must:</Typography>
      </Grid>
      <Grid container={true} spacing="18px" size={12}>
        <Grid
          container={true}
          size={12}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          {getIcon(errors, MIN_LENGTH_ERROR)}
          <Typography
            variant="body2"
            sx={{
              ...passwordClasses.passwordReqTypography,
              ...(isPasswordCriteriaMet(errors, touched.newPassword, MIN_LENGTH_ERROR) ? passwordClasses.passwordCriteriaMet(theme) : {}),
            }}
          >
            Be a minimum of 8 characters
          </Typography>
        </Grid>

        <Grid
          container={true}
          size={12}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          {getIcon(errors, AT_LEAST_ONE_NUMBER_ERROR)}
          <Typography
            variant="body2"
            sx={{
              ...passwordClasses.passwordReqTypography,
              ...(isPasswordCriteriaMet(errors, touched.newPassword, AT_LEAST_ONE_NUMBER_ERROR) ? passwordClasses.passwordCriteriaMet(theme) : {}),
            }}
          >
            Include at least one number
          </Typography>
        </Grid>

        <Grid
          container={true}
          size={12}
          sx={{
            alignItems: 'flex-start',
          }}
        >
          {getIcon(errors, TWO_CONSECUTIVE_CHARS_ERROR)}
          <Typography
            variant="body2"
            sx={{
              ...passwordClasses.passwordReqTypography,
              ...(isPasswordCriteriaMet(errors, touched.newPassword, TWO_CONSECUTIVE_CHARS_ERROR) ? passwordClasses.passwordCriteriaMet(theme) : {}),
            }}
          >
            Not contain more than two consecutive identical characters
          </Typography>
        </Grid>
      </Grid>
    </>
  )
}
